import { CollectionPermissions, MembershipRole, NamedCollection } from '../types';

/**
 * What the current user may do with a collection.
 *
 * slide-svc computes this per request (NamedCollectionService.evaluateAccess) and the UI uses the
 * server's answer as-is, so the role matrix lives in one place.
 *
 * Fallback for a server that predates shop-visible collections (no `permissions` field): every
 * collection it returns is the caller's own, so edit and delete are allowed, visibility can't be
 * changed (the endpoint doesn't exist there), and sharing keeps its management-role gate.
 */
export const collectionPermissions = (
  collection: NamedCollection,
  userRole: MembershipRole | null
): CollectionPermissions => {
  if (collection.permissions) {
    return collection.permissions;
  }
  return {
    canEdit: true,
    canDelete: true,
    canChangeVisibility: false,
    canShare: userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'PLATFORM_ADMIN',
  };
};

/** Label for who made a collection: null for your own, else the creator's name or a generic label. */
export const collectionCreatorLabel = (collection: NamedCollection): string | null => {
  if (collection.createdByMe !== false) {
    return null; // your own (or a pre-feature server, where everything returned is your own)
  }
  const name = collection.createdByDisplayName?.trim();
  return name ? name : 'Shop member';
};

/**
 * Data for the quick "add this slide to collections" picker: the collections the user may add to
 * (their own, plus shop-visible ones they can edit), and which of those already contain the slide.
 * Membership comes from each collection's slideRefs, so the picker needs no extra request.
 */
export const slidePickerData = (
  collections: NamedCollection[],
  slideId: string,
  userRole: MembershipRole | null
): { editable: NamedCollection[]; memberIds: Set<string> } => {
  const editable = collections.filter(c => collectionPermissions(c, userRole).canEdit);
  const memberIds = new Set(editable.filter(c => c.slideRefs.includes(slideId)).map(c => c.id));
  return { editable, memberIds };
};
